import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { Mark, Wordmark } from "@/components/ax/Mark";
import { Button } from "@/components/ax/Button";
import { useI18n } from "@/i18n";
import { auth } from "@/lib/firebase";
import { submitCopyrightReport } from "@/lib/reportStore";

export const LAPORAN_HAK_COPY = {
  id: {
    title: "Laporan hak cipta",
    lede: "Laporkan edisi yang Anda yakini melanggar hak cipta. Setiap aduan ditinjau manusia.",
    sourceLabel: "ID sumber atau judul edisi",
    sourcePlaceholder: "mis. doab_0123456789abcdef atau judul buku",
    categoryLabel: "Kategori",
    categoryCopyright: "Pelanggaran hak cipta",
    categoryLicense: "Lisensi tidak sesuai",
    categoryOther: "Lainnya",
    messageLabel: "Penjelasan (min. 10 karakter)",
    messagePlaceholder: "Tuliskan karya yang dilanggar dan bukti singkatnya",
    emailLabel: "Surel kontak (opsional)",
    emailPlaceholder: "nama@contoh.id",
    submit: "Kirim laporan",
    sending: "Mengirim...",
    needLoginTitle: "Masuk untuk melapor",
    needLoginBody: "Formulir aduan memerlukan akun masuk. Bukan pengguna AXORIS? Kirimkan aduan lewat surel.",
    loginLink: "Masuk",
    emailLinkLabel: "Lapor lewat surel",
    errInvalid: "Periksa kembali isian: ID sumber wajib, penjelasan 10–2000 karakter, surel harus valid.",
    errMissing: "Sumber tidak ditemukan. Periksa kembali ID atau judulnya.",
    errRateLimited: "Terlalu banyak laporan. Coba lagi nanti.",
    errWrite: "Laporan belum terkirim. Coba lagi.",
    successTitle: "Laporan diterima",
    successBody: "Terima kasih. Aduan Anda akan ditinjau manusia; edisi yang terbukti melanggar akan diturunkan.",
    backToRights: "Kembali ke hak konten",
    reportHint: "Aduan tidak menghapus edisi secara otomatis.",
  },
  en: {
    title: "Copyright report",
    lede: "Report an edition you believe infringes copyright. Every report is reviewed by a human.",
    sourceLabel: "Source ID or edition title",
    sourcePlaceholder: "e.g. doab_0123456789abcdef or book title",
    categoryLabel: "Category",
    categoryCopyright: "Copyright infringement",
    categoryLicense: "Incorrect license",
    categoryOther: "Other",
    messageLabel: "Explanation (min. 10 characters)",
    messagePlaceholder: "Describe the infringed work and brief evidence",
    emailLabel: "Contact email (optional)",
    emailPlaceholder: "name@example.com",
    submit: "Submit report",
    sending: "Sending...",
    needLoginTitle: "Sign in to report",
    needLoginBody: "The report form requires a signed-in account. Not an AXORIS user? Send your report by email.",
    loginLink: "Sign in",
    emailLinkLabel: "Report by email",
    errInvalid: "Check your input: source ID is required, explanation 10–2000 characters, email must be valid.",
    errMissing: "Source not found. Check the ID or title again.",
    errRateLimited: "Too many reports. Try again later.",
    errWrite: "The report was not sent. Try again.",
    successTitle: "Report received",
    successBody: "Thank you. Your report will be reviewed by a human; infringing editions will be taken down.",
    backToRights: "Back to content rights",
    reportHint: "A report does not remove an edition automatically.",
  },
};

const COPYRIGHT_MAILTO =
  "mailto:halo@axoris.id?subject=Laporan%20hak%20cipta%20Executive%20Library";

export default function LegalLaporanHak() {
  const { lang } = useI18n();
  const copy = LAPORAN_HAK_COPY[lang === "en" ? "en" : "id"];
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(() => auth.currentUser);
  const [sourceId, setSourceId] = useState(() => searchParams.get("sourceId") || "");
  const [category, setCategory] = useState("copyright");
  const [message, setMessage] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => onAuthStateChanged(auth, (u) => setUser(u)), []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    const res = await submitCopyrightReport({ sourceId, category, message, contactEmail });
    setBusy(false);
    if (res.reportId) {
      setDone(true);
      return;
    }
    if (res.error === "unauthenticated") {
      setUser(null);
      return;
    }
    setError(res.error === "missing" ? copy.errMissing
      : res.error === "rate-limited" ? copy.errRateLimited
      : res.error === "invalid" ? copy.errInvalid
      : copy.errWrite);
  };

  return (
    <div className="min-h-screen bg-ax-bg px-4 py-8 text-ax-text">
      <main className="mx-auto w-full max-w-[720px]" data-testid="laporan-hak-page">
        <Link to="/" aria-label="AXORIS" className="inline-flex items-center gap-3 text-ax-brand">
          <Mark size={24} />
          <Wordmark />
        </Link>
        <h1 className="type-h1 mt-8" data-testid="laporan-hak-title">{copy.title}</h1>
        <p className="mt-4 text-sm leading-6 text-ax-text-2" data-testid="laporan-hak-lede">{copy.lede}</p>

        {done ? (
          <section data-testid="laporan-hak-success" className="mt-8 rounded-[12px] border border-ax-border bg-ax-surface p-5" role="status">
            <h2 className="m-0 text-base font-semibold">{copy.successTitle}</h2>
            <p className="m-0 mt-2 text-sm leading-6 text-ax-text-2">{copy.successBody}</p>
            <p className="mt-4">
              <Link to="/legal/hak-konten" data-testid="laporan-hak-back-link" className="inline-flex min-h-[48px] items-center text-sm font-medium text-ax-link hover:underline">
                {copy.backToRights}
              </Link>
            </p>
          </section>
        ) : !user ? (
          <section data-testid="laporan-hak-login" className="mt-8 rounded-[12px] border border-ax-border bg-ax-surface p-5">
            <h2 className="m-0 text-base font-semibold">{copy.needLoginTitle}</h2>
            <p className="m-0 mt-2 text-sm leading-6 text-ax-text-2">{copy.needLoginBody}</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Link to="/masuk" data-testid="laporan-hak-login-link" className="inline-flex min-h-[48px] items-center rounded-[8px] border border-ax-border px-4 text-sm font-medium text-ax-link hover:underline">
                {copy.loginLink}
              </Link>
              <a href={COPYRIGHT_MAILTO} data-testid="laporan-hak-mailto" className="inline-flex min-h-[48px] items-center rounded-[8px] border border-ax-border px-4 text-sm font-medium text-ax-link hover:underline">
                {copy.emailLinkLabel}
              </a>
            </div>
          </section>
        ) : (
          <form onSubmit={onSubmit} data-testid="laporan-hak-form" className="mt-8 flex flex-col gap-5">
            <div>
              <label htmlFor="report-source" className="block text-sm font-medium">{copy.sourceLabel}</label>
              <input
                id="report-source"
                data-testid="laporan-hak-source-input"
                value={sourceId}
                onChange={(e) => setSourceId(e.target.value)}
                placeholder={copy.sourcePlaceholder}
                required
                className="mt-2 w-full rounded-[8px] border border-ax-border bg-ax-bg px-3 py-2 text-sm outline-none focus:border-ax-primary"
              />
            </div>
            <div>
              <label htmlFor="report-category" className="block text-sm font-medium">{copy.categoryLabel}</label>
              <select
                id="report-category"
                data-testid="laporan-hak-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="mt-2 w-full rounded-[8px] border border-ax-border bg-ax-bg px-3 py-2 text-sm outline-none focus:border-ax-primary"
              >
                <option value="copyright">{copy.categoryCopyright}</option>
                <option value="license">{copy.categoryLicense}</option>
                <option value="other">{copy.categoryOther}</option>
              </select>
            </div>
            <div>
              <label htmlFor="report-message" className="block text-sm font-medium">{copy.messageLabel}</label>
              <textarea
                id="report-message"
                data-testid="laporan-hak-message-input"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={copy.messagePlaceholder}
                rows={5}
                maxLength={2000}
                required
                className="mt-2 w-full rounded-[8px] border border-ax-border bg-ax-bg px-3 py-2 text-sm outline-none focus:border-ax-primary"
              />
            </div>
            <div>
              <label htmlFor="report-email" className="block text-sm font-medium">{copy.emailLabel}</label>
              <input
                id="report-email"
                data-testid="laporan-hak-email-input"
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder={copy.emailPlaceholder}
                className="mt-2 w-full rounded-[8px] border border-ax-border bg-ax-bg px-3 py-2 text-sm outline-none focus:border-ax-primary"
              />
            </div>
            {error && <p role="alert" data-testid="laporan-hak-error" className="m-0 text-sm leading-5 text-ax-error">{error}</p>}
            <p className="m-0 text-xs leading-5 text-ax-muted">{copy.reportHint}</p>
            <div>
              <Button type="submit" variant="primary" busy={busy} disabled={busy} data-testid="laporan-hak-submit">
                {busy ? copy.sending : copy.submit}
              </Button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
